/**
 * KHIYARSA (KMD): shared compile → preview → insert machinery, used by the
 * Import Markdown dialog and the Create-with-AI dialog. One implementation of
 * the debounced browser compile (real text measurement + icon loader), the
 * preview-canvas rendering, and the native-element insertion path.
 */
import { THEME } from "@excalidraw/common";
import { convertToExcalidrawElements } from "@excalidraw/element";
import { useApp } from "@excalidraw/excalidraw/components/App";
import { exportToCanvas } from "@excalidraw/utils";
import { useCallback, useEffect, useRef, useState } from "react";

import type { BinaryFiles } from "@excalidraw/excalidraw/types";
import type { NonDeletedExcalidrawElement } from "@excalidraw/element/types";

import type { CompileResult } from "../types";

export interface KmdPreviewApi {
  /** latest compile result (null while empty/cleared) */
  result: CompileResult | null;
  /** true when a compiled, insertable scene is staged */
  hasElements: boolean;
  /** insert the staged elements into the canvas; returns false if none */
  insert: () => boolean;
  /** drop staged elements + preview + result */
  reset: () => void;
}

export const useKmdPreview = ({
  text,
  active,
  previewRef,
}: {
  text: string;
  /** compile only while the owning dialog is open */
  active: boolean;
  previewRef: React.RefObject<HTMLDivElement | null>;
}): KmdPreviewApi => {
  const app = useApp();
  const [result, setResult] = useState<CompileResult | null>(null);
  const dataRef = useRef<{
    elements: readonly NonDeletedExcalidrawElement[];
    files: BinaryFiles | null;
  }>({ elements: [], files: null });
  const compileToken = useRef(0);

  useEffect(() => {
    if (!active) {
      return;
    }
    const token = ++compileToken.current;
    const timer = window.setTimeout(async () => {
      if (!text.trim()) {
        setResult(null);
        dataRef.current = { elements: [], files: null };
        previewRef.current?.replaceChildren();
        return;
      }
      // the KMD compiler (remark + layout engines + icon registry) is
      // code-split and loaded on first use, mirroring the mermaid chunk
      const [{ compileKmd }, { excalidrawMeasure }] = await Promise.all([
        import(".."),
        import("../measureBrowser"),
      ]);
      const compiled = await compileKmd(text, { measure: excalidrawMeasure });
      if (token !== compileToken.current) {
        return;
      }
      if (compiled.ok) {
        try {
          const converted = convertToExcalidrawElements(
            compiled.skeletons as any,
            { regenerateIds: false },
          );
          const elements = [
            ...converted,
            ...(compiled.extraElements as any[]),
          ] as readonly NonDeletedExcalidrawElement[];
          const files = (compiled.files ?? null) as BinaryFiles | null;
          dataRef.current = { elements, files };
          const parent = previewRef.current?.parentElement;
          if (previewRef.current && parent) {
            const canvas = await exportToCanvas({
              elements,
              files: files ?? {},
              exportPadding: 24,
              maxWidthOrHeight:
                Math.max(parent.offsetWidth, parent.offsetHeight, 300) *
                window.devicePixelRatio,
              appState: {
                exportWithDarkMode: app.state.theme === THEME.DARK,
              },
            });
            previewRef.current.replaceChildren(canvas);
          }
        } catch (error) {
          console.error("KMD preview failed", error);
          dataRef.current = { elements: [], files: null };
        }
      } else {
        dataRef.current = { elements: [], files: null };
        previewRef.current?.replaceChildren();
      }
      // set state last so renders observe the staged elements consistently
      setResult(compiled);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [text, active, app, previewRef]);

  const insert = useCallback((): boolean => {
    if (dataRef.current.elements.length === 0) {
      return false;
    }
    app.addElementsFromPasteOrLibrary({
      elements: dataRef.current.elements,
      files: dataRef.current.files,
      position: "center",
      fit: "scale-down",
    });
    return true;
  }, [app]);

  const reset = useCallback(() => {
    setResult(null);
    dataRef.current = { elements: [], files: null };
    previewRef.current?.replaceChildren();
  }, [previewRef]);

  return {
    result,
    hasElements: dataRef.current.elements.length > 0,
    insert,
    reset,
  };
};
